function getHoursText(dayInfo = {}) {
  if (dayInfo.hoursText) return dayInfo.hoursText;
  if (dayInfo.isClosed) return 'Closed';
  return [dayInfo.open, dayInfo.close].filter(Boolean).join(' - ') || '';
}

function summarizeHours(hoursByDay, days = []) {
  return days
    .map((day) => {
      const hoursText = hoursByDay[day.toLowerCase()];
      return hoursText ? `${day}: ${hoursText}` : '';
    })
    .filter(Boolean)
    .join(' | ');
}

function buildContactHours(hoursArray = []) {
  if (!Array.isArray(hoursArray) || hoursArray.length === 0) {
    return null;
  }

  const hoursByDay = hoursArray.reduce((acc, dayInfo = {}) => {
    const day = String(dayInfo.day || '').trim();
    if (!day) return acc;
    acc[day.toLowerCase()] = getHoursText(dayInfo) || 'Closed';
    return acc;
  }, {});

  const weekdays = summarizeHours(hoursByDay, ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']);
  const saturday = summarizeHours(hoursByDay, ['Saturday']);
  const sunday = summarizeHours(hoursByDay, ['Sunday']);
  const workingHours = summarizeHours(hoursByDay, ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']);

  return {
    object: {
      weekdays,
      saturday,
      sunday,
      ...hoursByDay
    },
    workingHours,
    sundayHours: sunday,
    summary: [workingHours, sunday].filter(Boolean).join(' | ')
  };
}

function getPhotoUrl(photo) {
  if (!photo) return null;
  if (typeof photo === 'string') return photo;
  return photo.url || photo.googleUrl || photo.sourceUrl || photo.image || null;
}

function normalizePhotoCollection(photos = []) {
  if (!Array.isArray(photos)) return [];
  return photos
    .map((photo, index) => {
      const url = getPhotoUrl(photo);
      if (!url) return null;
      if (typeof photo === 'string') {
        return {
          id: index + 1,
          url,
          name: `Photo ${index + 1}`,
          description: '',
          isRealPhoto: false
        };
      }
      return {
        ...photo,
        id: photo.id || index + 1,
        url
      };
    })
    .filter(Boolean);
}

function buildStructuredAddress(address) {
  const formatted = String(address || '').trim();
  if (!formatted) {
    return {
      country: '',
      street: '',
      city: '',
      formatted: ''
    };
  }

  const parts = formatted.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 1) {
    return {
      country: '',
      street: parts[0],
      city: '',
      formatted
    };
  }

  if (parts.length === 2) {
    return {
      country: parts[1],
      street: parts[0],
      city: parts[1],
      formatted
    };
  }

  const street = parts.slice(0, Math.max(1, parts.length - 3)).join(', ');
  const city = parts.slice(Math.max(1, parts.length - 3), parts.length - 1).join(', ');
  const country = parts[parts.length - 1];

  return {
    country,
    street: street || parts[0],
    city: city || parts.slice(1).join(', '),
    formatted
  };
}

function buildLatestUpdates(photos = [], businessName = 'Business') {
  return photos
    .slice(0, 8)
    .map((photo, index) => {
      const image = getPhotoUrl(photo);
      if (!image) return null;
      return {
        id: index + 1,
        image,
        date: 'Recent',
        title: photo.name || `${businessName} Update`,
        description: photo.description || 'Latest update',
        isRealPhoto: true
      };
    })
    .filter(Boolean);
}

function normalizeReviews(reviews = []) {
  if (!Array.isArray(reviews)) return [];
  return reviews.map((review, index) => ({
    ...review,
    id: review.id || index + 1,
    avatar: review.avatar || review.image || review.photo || null,
    comment:
      review.comment ||
      review.review ||
      review.content ||
      review.quote ||
      review.testimonial ||
      '',
    review:
      review.review ||
      review.comment ||
      review.content ||
      review.quote ||
      review.testimonial ||
      '',
    content:
      review.content ||
      review.review ||
      review.comment ||
      review.quote ||
      review.testimonial ||
      ''
  }));
}

function mapFeedPosts(gmbFeed) {
  const posts = Array.isArray(gmbFeed?.posts) ? gmbFeed.posts : [];
  return posts.map((post, index) => ({
    id: post.id || `feed-post-${index + 1}`,
    title: post.title || post.summary || `Update ${index + 1}`,
    excerpt: post.summary || post.content || '',
    image: post.media?.[0]?.googleUrl || post.media?.[0]?.sourceUrl || null,
    date: post.createTime || post.updateTime || null,
    category: post.topicType || 'Update',
    url: post.callToAction?.url || null
  }));
}

export function mergeWithLiveData(templateData, liveDataResponse) {
  if (!liveDataResponse?.data) return templateData;

  const merged = templateData && typeof templateData === 'object' ? { ...templateData } : {};
  const live = liveDataResponse.data;

  const testimonials = Array.isArray(live.testimonials) && live.testimonials.length > 0
    ? normalizeReviews(live.testimonials)
    : null;
  const photos = Array.isArray(live.photos) && live.photos.length > 0
    ? normalizePhotoCollection(live.photos)
    : null;
  const gmbFeed = live.gmbFeed && typeof live.gmbFeed === 'object'
    ? {
        ...live.gmbFeed,
        showSection: Boolean(
          live.gmbFeed.showSection !== undefined
            ? live.gmbFeed.showSection
            : Array.isArray(live.gmbFeed.posts) && live.gmbFeed.posts.length > 0
        )
      }
    : null;
  const businessHours = Array.isArray(live.businessHours) && live.businessHours.length > 0
    ? live.businessHours
    : null;
  const contactHours = buildContactHours(businessHours || merged.businessHours || merged.openingHours || []);

  const businessName =
    live.businessName ||
    merged.businessInfo?.name ||
    merged.businessName ||
    merged.name ||
    null;
  const description =
    live.description ||
    merged.businessInfo?.description ||
    merged.aboutUs?.description ||
    merged.description ||
    null;
  const address =
    live.address ||
    merged.contactInfo?.address ||
    merged.location?.address ||
    merged.businessInfo?.address ||
    merged.address ||
    null;
  const phone =
    live.phone ||
    merged.contactInfo?.phone ||
    merged.contact?.phone ||
    merged.businessInfo?.phone ||
    merged.businessInfo?.phoneNumber ||
    merged.phone ||
    null;
  const website =
    live.website ||
    merged.contactInfo?.website ||
    merged.contact?.website ||
    merged.businessInfo?.website ||
    merged.website ||
    null;
  const latitude =
    live.coordinates?.latitude ??
    merged.location?.lat ??
    merged.location?.latitude ??
    merged.businessInfo?.latitude ??
    merged.latitude ??
    null;
  const longitude =
    live.coordinates?.longitude ??
    merged.location?.lng ??
    merged.location?.longitude ??
    merged.businessInfo?.longitude ??
    merged.longitude ??
    null;
  const rating =
    live.rating ??
    merged.businessInfo?.rating ??
    merged.rating ??
    merged.location?.rating ??
    null;
  const reviewCount =
    live.reviewCount ??
    merged.businessInfo?.reviewCount ??
    merged.reviewCount ??
    merged.location?.reviewCount ??
    (testimonials ? testimonials.length : 0);
  const addressDetails = buildStructuredAddress(address);

  const updatedBusinessInfo = {
    ...(merged.businessInfo || {}),
    name: businessName || merged.businessInfo?.name || merged.businessName || null,
    description: description || merged.businessInfo?.description || merged.description || null,
    tagline:
      description ||
      merged.businessInfo?.tagline ||
      merged.businessInfo?.description ||
      merged.description ||
      null,
    heroSubtitle:
      description ||
      merged.businessInfo?.heroSubtitle ||
      merged.businessInfo?.description ||
      merged.description ||
      null,
    heroDescription:
      description ||
      merged.businessInfo?.heroDescription ||
      merged.businessInfo?.description ||
      merged.description ||
      null,
    address: address || merged.businessInfo?.address || merged.address || null,
    phone: phone || merged.businessInfo?.phone || merged.phone || null,
    phoneNumber:
      phone ||
      merged.businessInfo?.phoneNumber ||
      merged.businessInfo?.phone ||
      merged.phone ||
      null,
    website: website || merged.businessInfo?.website || merged.website || null,
    latitude,
    longitude,
    rating,
    reviewCount
  };

  const updatedContactInfo = {
    ...(merged.contactInfo || {}),
    address: address || merged.contactInfo?.address || updatedBusinessInfo.address || null,
    phone: phone || merged.contactInfo?.phone || updatedBusinessInfo.phone || null,
    website: website || merged.contactInfo?.website || updatedBusinessInfo.website || null,
    description:
      description ||
      merged.contactInfo?.description ||
      merged.description ||
      null,
    hours: contactHours?.object || merged.contactInfo?.hours || null,
    workingHours: contactHours?.workingHours || merged.contactInfo?.workingHours || null,
    sundayHours: contactHours?.sundayHours || merged.contactInfo?.sundayHours || null,
    details: {
      ...(merged.contactInfo?.details || {}),
      phone:
        phone ||
        merged.contactInfo?.details?.phone ||
        merged.contactInfo?.phone ||
        updatedBusinessInfo.phone ||
        null,
      website:
        website ||
        merged.contactInfo?.details?.website ||
        merged.contactInfo?.website ||
        updatedBusinessInfo.website ||
        null,
      address: {
        ...(merged.contactInfo?.details?.address || {}),
        ...addressDetails
      },
      hours: contactHours?.object || merged.contactInfo?.details?.hours || null
    }
  };

  const updatedLocation = {
    ...(merged.location || {}),
    address: address || merged.location?.address || updatedContactInfo.address || null,
    businessName: businessName || merged.location?.businessName || merged.businessName || null,
    lat: latitude ?? merged.location?.lat ?? merged.location?.latitude ?? null,
    lng: longitude ?? merged.location?.lng ?? merged.location?.longitude ?? null,
    latitude: latitude ?? merged.location?.latitude ?? merged.location?.lat ?? null,
    longitude: longitude ?? merged.location?.longitude ?? merged.location?.lng ?? null,
    latlng: {
      latitude: latitude ?? merged.location?.latlng?.latitude ?? null,
      longitude: longitude ?? merged.location?.latlng?.longitude ?? null
    },
    rating,
    reviewCount
  };

  const updatedContact = {
    ...(merged.contact || {}),
    address: address || merged.contact?.address || updatedContactInfo.address || null,
    phone: phone || merged.contact?.phone || updatedContactInfo.phone || null,
    website: website || merged.contact?.website || updatedContactInfo.website || null,
    hours: contactHours?.summary || merged.contact?.hours || null,
    description: description || merged.contact?.description || merged.description || null
  };

  const updatedTopContactInfo = {
    ...(merged.topContactInfo || {}),
    phone: phone || merged.topContactInfo?.phone || updatedContactInfo.phone || null,
    address: address || merged.topContactInfo?.address || updatedContactInfo.address || null,
    hours:
      contactHours?.summary ||
      merged.topContactInfo?.hours ||
      updatedContactInfo.workingHours ||
      null
  };

  const updatedFooter = {
    ...(merged.footer || {}),
    businessName: businessName || merged.footer?.businessName || null,
    description:
      description ||
      merged.footer?.description ||
      merged.footer?.tagline ||
      merged.description ||
      null,
    tagline:
      description ||
      merged.footer?.tagline ||
      merged.footer?.description ||
      merged.description ||
      null,
    website: website || merged.footer?.website || null,
    contactInfo: {
      ...(merged.footer?.contactInfo || {}),
      address: address || merged.footer?.contactInfo?.address || updatedContactInfo.address || null,
      phone: phone || merged.footer?.contactInfo?.phone || updatedContactInfo.phone || null,
      website: website || merged.footer?.contactInfo?.website || updatedContactInfo.website || null
    }
  };

  const updatedAboutUs = {
    ...(merged.aboutUs || {}),
    description:
      description ||
      merged.aboutUs?.description ||
      merged.aboutUs?.content ||
      merged.description ||
      null,
    content:
      description ||
      merged.aboutUs?.content ||
      merged.aboutUs?.description ||
      merged.description ||
      null
  };

  return {
    ...merged,
    businessName: businessName || merged.businessName || null,
    name: businessName || merged.name || null,
    description: description || merged.description || null,
    address: address || merged.address || null,
    phone: phone || merged.phone || null,
    website: website || merged.website || null,
    latitude,
    longitude,
    testimonials: testimonials || merged.testimonials || merged.reviews || [],
    reviews: testimonials || normalizeReviews(merged.reviews || merged.testimonials || []),
    photos: photos || normalizePhotoCollection(merged.photos || merged.images || []),
    images:
      photos
        ? photos.map((photo) => getPhotoUrl(photo)).filter(Boolean)
        : (Array.isArray(merged.images) ? merged.images : []),
    latestUpdates:
      photos && photos.length > 0
        ? buildLatestUpdates(photos, businessName || 'Business')
        : merged.latestUpdates,
    gmbFeed: gmbFeed || merged.gmbFeed || { showSection: false, posts: [] },
    feedPosts:
      gmbFeed && Array.isArray(gmbFeed.posts) && gmbFeed.posts.length > 0
        ? mapFeedPosts(gmbFeed)
        : merged.feedPosts,
    businessHours: businessHours || merged.businessHours || merged.openingHours || [],
    openingHours: businessHours || merged.openingHours || merged.businessHours || [],
    rating,
    reviewCount,
    businessInfo: updatedBusinessInfo,
    contactInfo: updatedContactInfo,
    location: updatedLocation,
    contact: updatedContact,
    topContactInfo: updatedTopContactInfo,
    footer: updatedFooter,
    aboutUs: updatedAboutUs,
    _liveDataUpdatedAt: live.lastUpdated || merged._liveDataUpdatedAt || null
  };
}
